import config from "config/config";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { useBreadcrumb } from "hooks/useBreadcrumb";
import { useAuthorizationCheck } from "hooks/useAuthorizationCheck";
import { getCorrespondenceList } from "redux/slices/getCorrespondenceListSlice";
import moment from "moment";

import {
  isFlagEnabled,
  isWholeLifePolicy,
  getAgentsFromPolicy,
  isPolicyInactiveByPolicyStatus,
  isOldAlicUlPolicy,
  isInterestSensitiveWholeLifePolicy,
} from "js/utilities";
import { stringFormatingToPascal } from "js/formatting";
import { usePolicyDetailLinks } from "js/placeholderData/policyDetail";

import Carousel from "components/General/Carousel";
import AgentCard from "components/Policy/Detail/AgentCard";
import SimpleDataRow from "components/General/SimpleDataRow";
import PolicyValue from "components/Policy/Detail/PolicyValue";
import PaymentSummaryCard from "components/Policy/Detail/PaymentSummary";
import OwnerAndInsuredCard from "components/Policy/Detail/OwnerAndInsured";
import BeneficiariesCard from "components/Policy/Detail/BeneficiariesCard";
import ThingsYouCanDoCard from "components/Policy/Detail/ThingsYouCanDoCard";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import CoverageAndDocumentsCard from "components/Policy/Detail/CoverageAndDocumentsCard";
import UserNotificationDialogOnLogin from "components/Profile/Dialogs/UserNotificationDialogOnLogin";

export default function PolicyDetails() {
  const { t } = useTranslation();
  const { policy_id } = useParams();
  const links = usePolicyDetailLinks();
  const { user } = useSelector((state) => state.user);
  const { policies, claims, isLoading } = useSelector((state) => state.data);
  const dispatch = useDispatch();

  const [policy, setPolicy] = useState();
  const [firstName, setFirstName] = useState();
  const [openUserNotificationDialog, setOpenUserNotificationDialog] =
    useState(false);
  const [alerts, setAlerts] = useState([]);
  const [isCorrespondenceLoading, setIsCorrespondenceLoading] = useState(true);

  const viewAgent = useAuthorizationCheck(11);
  const viewBeneficiaries = useAuthorizationCheck(9);
  const viewServiceandClaim = useAuthorizationCheck(4);
  const viewOwnerAndInsured = useAuthorizationCheck(8);
  const viewPaymentSummary = useAuthorizationCheck(10);
  const viewCoverageAndDocumentl = useAuthorizationCheck(7);
  const selectedPolicy = policies.find((x) => x.contractID === policy_id);

  const getOldPolicy = (contrctId) => {
    if (policies?.length > 0) {
      let policy = policies.find((p) => p.contractID === contrctId);
      if (policy) {
        return policy.oldPolicy;
      }
    }
    return "";
  };

  useEffect(() => {
    if (!isLoading) {
      if (policies) {
        let selectedPolicy = policies.find((x) => x.contractID === policy_id);
        let party = selectedPolicy?.parties?.find(
          (x) => x.person?.taxId === user.identityNumber
        );
        setFirstName(stringFormatingToPascal(party?.person?.firstName));
        setPolicy(selectedPolicy);
      }
    }

    if (
      config.E_DELIVERY_ENABLE === "true" &&
      user.notificationPreference === 0
    ) {
      setOpenUserNotificationDialog(true);
    }
  }, [isLoading]);

  useEffect(() => {
    if (selectedPolicy) {
      dispatch(
        getCorrespondenceList({
          policyId: [selectedPolicy?.contractID],
          agentId: "",
          beginDate: moment()
            .subtract(parseInt(config.ALERT_AND_DOCUMENTS_DURATION), "months")
            .format("MM/DD/YYYY"),
          endDate: moment().format("MM/DD/YYYY"),
        })
      )
        .then((response) => {
          const RESTRICTED_CORRESPONDENCE_TYPE =
            config.RESTRICTED_CORRESPONDENCE_TYPE.split(",");
          let alertArray = [];
          if (response?.meta?.requestStatus === "fulfilled") {
            response.payload.allAlerts.map((a) => {
              if (
                !RESTRICTED_CORRESPONDENCE_TYPE.includes(
                  a.document.typeOfDocument?.toUpperCase()
                )
              ) {
                if (a?.type !== "alert") {
                  alertArray.push({
                    oldPolicy: getOldPolicy(a?.policy?.contractID),
                    ...a,
                  });
                } else {
                  alertArray.push(a);
                }
              }
            });
            setAlerts(alertArray);
          } else {
            if (
              response.payload[0] ===
              t("AlertsCorrespondence-correspondence-error-message-1")
            ) {
              setAlerts([]);
            }
          }
          setIsCorrespondenceLoading(false);
        })
        .catch((error) => {
          setIsCorrespondenceLoading(false);
          console.log("Error", error.message);
        });
    }
  }, [policies, selectedPolicy]);

  // setup breadcrumb for this page
  useBreadcrumb(
    [
      { name: t("CustomerPolicyDetailsBreadcrumb-home"), url: "/policies" },
      {
        name: t("CustomerPolicyDetailsBreadcrumb-life-policy-details"),
        url: "#",
      },
    ],
    [policy, firstName]
  );

  const agentCard = (agents) => {
    const agent = getAgentsFromPolicy(agents);

    return agent?.length ? <AgentCard data={agent} /> : null;
  };

  const hasDirectBillDocuments = alerts.some(
    (alert) => alert.document.typeOfDocument === "DIRECTBILL"
  );

  return (
    <EverlakeLoadingOverlay
      isLoading={isLoading || isCorrespondenceLoading}
      loadingText={t("CustomerPolicyDetails-loadingText")}
    >
      {policies && policies.length > 0 ? (
        <>
          <UserNotificationDialogOnLogin
            showDialog={openUserNotificationDialog}
            setShowDialog={setOpenUserNotificationDialog}
          />
          <div className="container mb-8">
            <h1>{t("CustomerPolicyDetails-life-policy-details-title")}</h1>
            <p>{t("CustomerPolicyDetails-subTitle")}</p>
            <hr />
          </div>

          <Carousel policies={policies} />

          <div className="container ui-component-spacing">
            <div className="flex flex-col lg:flex-row">
              <div className="lg:w-2/3 lg:pr-3">
                {viewCoverageAndDocumentl && policy && (
                  <CoverageAndDocumentsCard data={policy} />
                )}

                {isWholeLifePolicy(policy?.product?.type) &&
                  isFlagEnabled("ENABLE_POLICY_VALUE_FOR_WL") && (
                    <PolicyValue data={policy} />
                  )}

                {isOldAlicUlPolicy(policy) && <PolicyValue data={policy} />}

                {viewOwnerAndInsured && policy && (
                  <OwnerAndInsuredCard data={policy} />
                )}

                {viewBeneficiaries && policy && (
                  <BeneficiariesCard data={policy} />
                )}
                {viewPaymentSummary &&
                  policy &&
                  !isInterestSensitiveWholeLifePolicy(policy) &&
                  !isPolicyInactiveByPolicyStatus(policy?.status) &&
                  !["reducedpaidup", "paid-up", "extended term"].includes(
                    (policy?.status).toLowerCase()
                  ) && (
                    <PaymentSummaryCard data={policy} policy_id={policy_id} />
                  )}

                {viewAgent && policy?.agents && agentCard(policy)}
              </div>

              <div className="lg:w-1/3 lg:pl-3">
                <ThingsYouCanDoCard
                  data={links}
                  policy={policy}
                  hasDirectBillDocuments={hasDirectBillDocuments}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 md:gap-x-6 lg:grid-cols-1 lg:gap-0">
                  {viewServiceandClaim &&
                    claims &&
                    claims.filter((p) => p.contractID === policy_id).length >
                      0 && (
                      <div className="mb-4 ui-card md:mb-6">
                        <div className="px-4 pt-4 pb-1 border-b border-theme-rule-light">
                          <h2 className="text-sm font-semibold font-body">
                            {t("CustomerPolicyDetails-myClaims")}
                          </h2>
                        </div>
                        <div>
                          {claims
                            .filter((p) => p.contractID === policy_id)
                            .map((claim, idx) => (
                              <SimpleDataRow
                                type="customerServicesAndClaims"
                                key={`Customer claim - ${idx}`}
                                data={claim}
                              />
                            ))}
                        </div>
                        <Link
                          to="/services-claims/claims"
                          className="text-center ui-card-btn-more"
                          aria-label={t(
                            "CustomerPolicyDetails-myClaims-arialLabel"
                          )}
                        >
                          {t("CustomerPolicyDetails-viewMore")}
                        </Link>
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="container mb-8">
            <p> {t("CustomerPolicyDetails-noPolicies")}</p>
          </div>
        </>
      )}
    </EverlakeLoadingOverlay>
  );
}
